import React from "react";
import {
  CreditCard,
  DollarSign,
  AlertTriangle,
  FileText,
  Clock,
  ShieldCheck,
  Check,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import { Textarea } from "src/components/ui/TextArea";
import FileUpload from "src/components/ui/FileUpload";

interface EnrollmentPaymentSectionProps {
  defaultPrice: number;
  totalSessions: string;
  onTotalSessionsChange: (val: string) => void;
  actualPricePerSession: string;
  onActualPriceChange: (val: string) => void;
  overrideReason: string;
  onOverrideReasonChange: (val: string) => void;
  paymentKind: "full" | "installment";
  onPaymentKindChange: (kind: "full" | "installment") => void;
  installmentAmount: string;
  onInstallmentAmountChange: (val: string) => void;
  currency: string;
  onCurrencyChange: (val: string) => void;
  paymentMethod: string;
  onPaymentMethodChange: (val: string) => void;
  paymentReference: string;
  onPaymentReferenceChange: (val: string) => void;
  paidAt: string;
  onPaidAtChange: (val: string) => void;
  notes: string;
  onNotesChange: (val: string) => void;
  invoiceFile: File | null;
  onInvoiceSelect: (file: File) => void;
  onInvoiceRemove: () => void;
}

export const EnrollmentPaymentSection: React.FC<EnrollmentPaymentSectionProps> = ({
  defaultPrice,
  totalSessions,
  onTotalSessionsChange,
  actualPricePerSession,
  onActualPriceChange,
  overrideReason,
  onOverrideReasonChange,
  paymentKind,
  onPaymentKindChange,
  installmentAmount,
  onInstallmentAmountChange,
  currency,
  onCurrencyChange,
  paymentMethod,
  onPaymentMethodChange,
  paymentReference,
  onPaymentReferenceChange,
  paidAt,
  onPaidAtChange,
  notes,
  onNotesChange,
  invoiceFile,
  onInvoiceSelect,
  onInvoiceRemove,
}) => {
  const sessions = Number(totalSessions) || 0;
  const actualRate = Number(actualPricePerSession) || 0;
  const totalPurchasePrice = defaultPrice * sessions;
  const actualPurchasePrice = actualRate * sessions;
  const isOverridden = defaultPrice > 0 && Math.abs(actualRate - defaultPrice) > 0.001;
  const discountAmount = totalPurchasePrice - actualPurchasePrice;

  return (
    <Card className="border-slate-200/90 shadow-sm overflow-hidden bg-white">
      <CardHeader className="bg-slate-50/60 border-b border-slate-100 py-4 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
              3
            </div>
            <div>
              <CardTitle className="text-sm font-bold text-slate-900">
                Commercial Terms & Payment Verification
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-0.5">
                Record verified offline payment details and attach a signed legal PDF invoice.
              </CardDescription>
            </div>
          </div>
          <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
            STEP 03/03
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        {/* Session Count & Rate Overrides */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Total Sessions</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Input
              type="number"
              min="1"
              step="1"
              placeholder="e.g. 20"
              value={totalSessions}
              onChange={(e) => onTotalSessionsChange(e.target.value)}
              className="text-xs font-mono font-semibold bg-white border-slate-200 rounded-xl"
              required
            />
            <p className="text-[11px] text-slate-400">Total number of sessions contracted.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Catalog Rate</Label>
            <div className="h-10 px-3 flex items-center bg-slate-100 border border-slate-200 rounded-xl text-xs font-mono text-slate-600 cursor-not-allowed">
              ${defaultPrice.toFixed(2)} / session
            </div>
            <p className="text-[11px] text-slate-400">Standard catalog unit rate.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Actual Rate / Session</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-xs font-mono text-slate-400">$</span>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="e.g. 65.00"
                value={actualPricePerSession}
                onChange={(e) => onActualPriceChange(e.target.value)}
                className="pl-7 text-xs font-mono font-semibold text-purple-700 bg-white border-slate-200 rounded-xl"
                required
              />
            </div>
            <p className="text-[11px] text-slate-400">Agreed commercial rate per session.</p>
          </div>
        </div>

        {/* Price Override Warning Box */}
        {isOverridden && (
          <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>
                Commercial Rate Override ({discountAmount > 0 ? "Discounted" : "Premium"} vs Catalog)
              </span>
            </div>
            <p className="text-[11px] text-amber-800">
              Catalog total is ${totalPurchasePrice.toFixed(2)}; actual contracted total is ${actualPurchasePrice.toFixed(2)} (difference: ${Math.abs(discountAmount).toFixed(2)}).
            </p>
            <div className="pt-1">
              <Label className="text-xs font-semibold text-amber-900 block mb-1">
                Override Justification (Required for Audit Trail) <span className="text-red-600">*</span>
              </Label>
              <Textarea
                rows={2}
                placeholder="State the approved commercial rationale, coupon reference, or authorizing director..."
                value={overrideReason}
                onChange={(e) => onOverrideReasonChange(e.target.value)}
                className="text-xs bg-white border-amber-300 rounded-lg placeholder-amber-400/80"
              />
            </div>
          </div>
        )}

        {/* Settlement Type: Full vs Installment */}
        <div className="pt-2 border-t border-slate-100 space-y-2.5">
          <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5 text-purple-600" />
            <span>Offline Payment Collection Mode</span>
            <span className="text-red-500 font-bold">*</span>
          </Label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className={`flex items-start p-3 rounded-xl border cursor-pointer transition ${
                paymentKind === "full"
                  ? "bg-purple-50/50 border-purple-300 shadow-xs"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="paymentKind"
                checked={paymentKind === "full"}
                onChange={() => onPaymentKindChange("full")}
                className="mt-0.5 text-purple-600 focus:ring-purple-500"
              />
              <div className="ml-3">
                <span className="block text-xs font-bold text-slate-900">Paid in Full</span>
                <span className="block text-[11px] text-slate-500">
                  Full purchase price (${actualPurchasePrice.toFixed(2)}) collected up-front.
                </span>
              </div>
            </label>

            <label
              className={`flex items-start p-3 rounded-xl border cursor-pointer transition ${
                paymentKind === "installment"
                  ? "bg-purple-50/50 border-purple-300 shadow-xs"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="paymentKind"
                checked={paymentKind === "installment"}
                onChange={() => onPaymentKindChange("installment")}
                className="mt-0.5 text-purple-600 focus:ring-purple-500"
              />
              <div className="ml-3">
                <span className="block text-xs font-bold text-slate-900">First Installment</span>
                <span className="block text-[11px] text-slate-500">
                  Collect deposit today; invoice remaining balance.
                </span>
              </div>
            </label>
          </div>

          {/* Installment input */}
          {paymentKind === "installment" && (
            <div className="p-3 bg-purple-50/30 rounded-xl border border-purple-100 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1">
                <span>First Installment Amount Received</span>
                <span className="text-red-500 font-bold">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-xs font-mono text-slate-400">$</span>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="e.g. 500.00"
                  value={installmentAmount}
                  onChange={(e) => onInstallmentAmountChange(e.target.value)}
                  className="pl-7 text-xs font-mono font-semibold bg-white border-slate-200 rounded-xl"
                  required
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Must be greater than zero and less than the total purchase price (${actualPurchasePrice.toFixed(2)}).
              </p>
            </div>
          )}
        </div>

        {/* Currency, Payment Method, Reference, Timestamp */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Currency</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Input
              value={currency}
              maxLength={3}
              onChange={(e) => onCurrencyChange(e.target.value.toUpperCase())}
              placeholder="USD"
              className="text-xs font-mono uppercase bg-white border-slate-200 rounded-xl"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Payment Method</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Input
              value={paymentMethod}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
              placeholder="Bank Wire, Cheque, Cash..."
              className="text-xs bg-white border-slate-200 rounded-xl"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Reference / UTR</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Input
              value={paymentReference}
              onChange={(e) => onPaymentReferenceChange(e.target.value)}
              placeholder="e.g. UTR-99882103"
              className="text-xs font-mono bg-white border-slate-200 rounded-xl"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <span>Paid Timestamp</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Input
              type="datetime-local"
              value={paidAt}
              onChange={(e) => onPaidAtChange(e.target.value)}
              className="text-xs bg-white border-slate-200 rounded-xl"
              required
            />
          </div>
        </div>

        {/* Verification Notes */}
        <div className="space-y-1.5 pt-1">
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
            <span>Finance Audit & Verification Notes</span>
            <span className="text-red-500 font-bold">*</span>
          </Label>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Record who verified the deposit, bank statement batch date, or finance ticket number..."
            className="text-xs bg-white border-slate-200 rounded-xl"
            required
          />
        </div>

        {/* Invoice PDF Upload */}
        <div className="space-y-1.5 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-purple-600" />
              <span>Certified Invoice PDF</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <span className="text-[11px] text-slate-400">PDF format · Maximum 10 MB</span>
          </div>

          <FileUpload
            onFileSelect={onInvoiceSelect}
            onFileRemove={onInvoiceRemove}
            fileName={invoiceFile?.name}
            accept=".pdf,application/pdf"
            maxSize={10 * 1024 * 1024}
            buttonText="Attach signed invoice PDF"
            required
          />
        </div>
      </CardContent>
    </Card>
  );
};

